import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQuery } from 'react-query';
import api, { adminAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotification } from '../../../contexts/NotificationContext';
import LoadingSpinner from '../../../components/LoadingSpinner';
import StepProgress from '../../../components/StepProgress';
import { FiUpload, FiPlus, FiTrash2, FiAlertCircle, FiCheck, FiX, FiPackage } from 'react-icons/fi';

const AdminCreateInboundProject = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('companyId');
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [step, setStep] = useState(1);
  const [inputMethod, setInputMethod] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [lines, setLines] = useState([]);
  const [activeLineIndex, setActiveLineIndex] = useState(0);
  const [lineErrors, setLineErrors] = useState({});
  const [skuValidation, setSkuValidation] = useState({});
  const [expandedItems, setExpandedItems] = useState({});
  const [skuTimeouts, setSkuTimeouts] = useState({});
  const [skuSuggestions, setSkuSuggestions] = useState({});
  const [activeSkuSuggestionLine, setActiveSkuSuggestionLine] = useState(null);
  const linesRef = useRef(lines);
  const skuRequestRef = useRef({});

  // Redirect if no company selected
  useEffect(() => {
    if (!companyId) {
      showError('Please select a company first');
      navigate('/admin/requests/create?type=incoming');
    }
  }, [companyId, navigate, showError]);

  // Get company info
  const { data: companyData } = useQuery(
    ['admin-customer', companyId],
    () => adminAPI.getCustomer(companyId),
    { enabled: !!companyId }
  );
  const company = companyData?.data || companyData;

  // Get warehouses (internal warehouses for admin)
  const { data: warehousesData } = useQuery(
    'warehouses',
    async () => {
      const response = await api.get('/warehouses');
      return response.data?.data || response.data || [];
    },
    { staleTime: 300000 }
  );
  const warehouses = Array.isArray(warehousesData) ? warehousesData : [];

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(skuTimeouts).forEach(timeoutId => {
        if (timeoutId) clearTimeout(timeoutId);
      });
    };
  }, [skuTimeouts]);

  useEffect(() => {
    linesRef.current = lines;
  }, [lines]);

  const validationSchema = Yup.object({
    name: Yup.string().required('Manifest name is required'),
    destination_warehouse_id: Yup.string().required('Destination warehouse is required'),
    expected_arrival: Yup.date().nullable(),
    notes: Yup.string()
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      destination_warehouse_id: '',
      expected_arrival: '',
      notes: '',
      // Receiving instructions for the whole manifest. Hydrated from the
      // selected customer's company-level defaults via the useEffect
      // below; the backend also falls back to the same defaults for any
      // key that arrives undefined, so leaving these untouched is safe.
      receivingInstructions: {
        require_serial_numbers_on_receive: false,
        require_mac_addresses_on_receive: false,
        relabel_on_receive: false
      }
    },
    validationSchema,
    onSubmit: async () => {}
  });

  // Pre-populate the receiving instruction checkboxes from the selected
  // customer's configured defaults so the admin sees the same state the
  // customer would when submitting directly. Uses the detail returned by
  // the `admin-customer` query above rather than a second fetch.
  useEffect(() => {
    const prefs = company?.receivingPreferences
      || company?.data?.receivingPreferences
      || company?.receiving_preferences
      || null;
    if (prefs && typeof prefs === 'object') {
      formik.setFieldValue('receivingInstructions', {
        require_serial_numbers_on_receive: prefs.require_serial_numbers_on_receive === true,
        require_mac_addresses_on_receive: prefs.require_mac_addresses_on_receive === true,
        relabel_on_receive: prefs.relabel_on_receive === true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id]);

  // Create project mutation - uses admin endpoint
  const createProjectMutation = useMutation(
    (projectData) => api.post('/admin/inbound-projects', projectData),
    {
      onSuccess: (response) => {
        showSuccess('Inbound manifest created successfully!');
        navigate(`/admin/inbound-projects/${response.data.project.id}`);
      },
      onError: (error) => {
        showError(error.response?.data?.message || 'Failed to create project');
      }
    }
  );

  // CSV upload mutation
  const uploadCsvMutation = useMutation(
    (formData) => api.post('/inbound-projects/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    {
      onSuccess: (response) => {
        setCsvPreview(response.data);
        setLines(response.data.data || []);
        setStep(3);
        showSuccess('CSV file processed successfully!');
      },
      onError: (error) => {
        showError(error.response?.data?.message || 'Failed to process CSV file');
      }
    }
  );

  const normalizeSku = (sku) => String(sku || '').trim().toLowerCase();

  const getInventorySearchResults = async (sku) => {
    const normalizedSku = normalizeSku(sku);
    if (!normalizedSku || !companyId) return [];

    const response = await api.get('/inventory/search', { params: { query: normalizedSku, companyId } });
    return Array.isArray(response.data) ? response.data : response.data?.data || [];
  };

  const applyExistingSkuMatch = (line, matchedItem) => ({
    ...line,
    item_name: line.item_name || matchedItem.productName || matchedItem.product_name || '',
    original_description: line.original_description || matchedItem.description || ''
  });

  const selectSkuSuggestion = (lineIndex, item) => {
    const selectedSku = item.productSku || item.product_sku || '';
    setLines(prevLines => {
      const updatedLines = [...prevLines];
      updatedLines[lineIndex] = applyExistingSkuMatch(
        { ...updatedLines[lineIndex], original_sku: selectedSku },
        item
      );
      linesRef.current = updatedLines;
      return updatedLines;
    });
    setSkuValidation(prev => ({ ...prev, [lineIndex]: 'valid' }));
    setSkuSuggestions(prev => ({ ...prev, [lineIndex]: [] }));
    setActiveSkuSuggestionLine(null);
  };

  // Validate SKU against customer's inventory
  const validateSku = async (sku, lineIndex) => {
    const normalizedSku = normalizeSku(sku);
    const requestId = (skuRequestRef.current[lineIndex] || 0) + 1;
    skuRequestRef.current[lineIndex] = requestId;

    if (!normalizedSku) {
      setSkuValidation(prev => ({ ...prev, [lineIndex]: null }));
      setSkuSuggestions(prev => ({ ...prev, [lineIndex]: [] }));
      return;
    }

    if (normalizedSku.length < 3) {
      setSkuValidation(prev => ({ ...prev, [lineIndex]: null }));
      setSkuSuggestions(prev => ({ ...prev, [lineIndex]: [] }));
      return;
    }

    try {
      const items = await getInventorySearchResults(normalizedSku);
      if (skuRequestRef.current[lineIndex] !== requestId || normalizeSku(linesRef.current[lineIndex]?.original_sku) !== normalizedSku) {
        return;
      }

      const exactMatches = items.filter((item) => normalizeSku(item.productSku || item.product_sku) === normalizedSku);
      const matchedItem = exactMatches.length === 1 ? exactMatches[0] : null;
      setSkuSuggestions(prev => ({
        ...prev,
        [lineIndex]: matchedItem ? [] : items
      }));

      if (matchedItem) {
        setSkuValidation(prev => ({ ...prev, [lineIndex]: 'valid' }));
        setLines(prevLines => {
          if (normalizeSku(prevLines[lineIndex]?.original_sku) !== normalizedSku) return prevLines;
          const updatedLines = [...prevLines];
          updatedLines[lineIndex] = applyExistingSkuMatch(updatedLines[lineIndex], matchedItem);
          linesRef.current = updatedLines;
          return updatedLines;
        });
      } else {
        setSkuValidation(prev => ({ ...prev, [lineIndex]: 'unmatched' }));
      }
    } catch {
      setSkuValidation(prev => ({ ...prev, [lineIndex]: 'error' }));
    }
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setCsvFile(file);
  };

  const handleCsvUpload = () => {
    if (!csvFile) return;
    const formData = new FormData();
    formData.append('file', csvFile);
    uploadCsvMutation.mutate(formData);
  };

  const addManualLine = () => {
    if (lines.length > 0 && !validateManualLine(activeLineIndex)) {
      showError('Save the current item before adding another item.');
      return;
    }

    const updatedExpansion = { ...expandedItems };
    lines.forEach((line, index) => {
      if (line.original_sku && line.qty_expected > 0) {
        updatedExpansion[index] = false;
      }
    });
    const newLine = {
      vendor: '',
      original_sku: '',
      item_name: '',
      original_description: '',
      qty_expected: 1,
      expected_package_type: 'Each',
      gross_weight_lbs: null,
      // Insurance / project tracking -- parity with the customer form so
      // admin-created manifests capture the same per-line metadata.
      declared_unit_value: null,
      project_name: '',
      project_code: '',
      notes: ''
    };
    const newIndex = lines.length;
    setLines([...lines, newLine]);
    linesRef.current = [...lines, newLine];
    setActiveLineIndex(newIndex);
    updatedExpansion[newIndex] = true;
    setExpandedItems(updatedExpansion);
  };

  const toggleItemExpansion = (index) => {
    setExpandedItems(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const getManualLineErrors = (line) => {
    const errors = {};
    if (!String(line?.original_sku || '').trim()) errors.original_sku = 'SKU is required';
    if (!line?.qty_expected || Number(line.qty_expected) < 1) errors.qty_expected = 'Quantity is required';
    return errors;
  };

  const validateManualLine = (index) => {
    const errors = getManualLineErrors(linesRef.current[index]);
    setLineErrors(prev => ({ ...prev, [index]: errors }));
    return Object.keys(errors).length === 0;
  };

  const saveManualLine = (index) => {
    if (!validateManualLine(index)) return false;
    setExpandedItems(prev => ({ ...prev, [index]: false }));
    return true;
  };

  const getLineStatus = (line, index) => {
    if (Object.keys(lineErrors[index] || {}).length > 0) return 'Needs value';
    const errors = getManualLineErrors(line);
    return Object.keys(errors).length > 0 ? 'Draft' : 'Saved';
  };

  const updateLine = (index, field, value) => {
    const updatedLines = [...lines];
    updatedLines[index] = { ...updatedLines[index], [field]: value };
    linesRef.current = updatedLines;
    setLines(updatedLines);
    if (lineErrors[index]?.[field]) {
      const nextErrors = { ...(lineErrors[index] || {}) };
      delete nextErrors[field];
      setLineErrors(prev => ({ ...prev, [index]: nextErrors }));
    }
    if (field === 'original_sku') {
      setSkuValidation(prev => ({ ...prev, [index]: null }));
      setActiveSkuSuggestionLine(index);
      if (skuTimeouts[index]) clearTimeout(skuTimeouts[index]);
      const timeoutId = setTimeout(() => validateSku(value, index), 500);
      setSkuTimeouts(prev => ({ ...prev, [index]: timeoutId }));
    }
  };

  const removeLine = (index) => {
    const updatedLines = lines.filter((_, i) => i !== index);
    linesRef.current = updatedLines;
    setLines(updatedLines);
    setActiveLineIndex(Math.max(0, Math.min(activeLineIndex, updatedLines.length - 1)));
    const newValidation = { ...skuValidation };
    delete newValidation[index];
    setSkuValidation(newValidation);
    const newSuggestions = { ...skuSuggestions };
    delete newSuggestions[index];
    setSkuSuggestions(newSuggestions);
    const newLineErrors = { ...lineErrors };
    delete newLineErrors[index];
    setLineErrors(newLineErrors);
  };

  const handleSubmit = () => {
    if (!formik.isValid) {
      formik.validateForm();
      return;
    }
    if (lines.length === 0) {
      showError('Please add at least one line item');
      return;
    }
    const missingSkus = lines.filter(line => !line.original_sku);
    if (missingSkus.length > 0) {
      showError('Each line must have a SKU');
      return;
    }
    const projectData = {
      ...formik.values,
      company_id: companyId,
      expected_arrival_date: formik.values.expected_arrival || null,
      lines
    };
    delete projectData.expected_arrival;
    createProjectMutation.mutate(projectData);
  };

  const handleProceedToReview = () => {
    if (lines.length === 0) return;

    if (inputMethod === 'csv') {
      setStep(3);
      return;
    }

    if (!validateManualLine(activeLineIndex)) {
      showError('Save the current item before reviewing.');
      return;
    }

    setStep(3);
  };

  const getSkuValidationIcon = (status) => {
    switch (status) {
      case 'valid':
        return <FiCheck className="w-4 h-4 text-green-500" title="SKU exists" />;
      case 'unmatched':
        return <FiAlertCircle className="w-4 h-4 text-yellow-500" title="New SKU — will be created" />;
      case 'error':
        return <FiX className="w-4 h-4 text-red-500" title="Validation error" />;
      default:
        return null;
    }
  };

  if (!companyId) return null;

  return (
    <div className="py-6">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Inbound Manifest</h1>
          <p className="text-gray-600">
            Creating manifest for <span className="font-medium">{company?.name || 'Loading...'}</span>
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/requests?type=incoming,crossdock,inbound_manifest')}
          className="text-gray-600 hover:text-gray-800"
        >
          Cancel
        </button>
      </div>

      {/* Progress Steps */}
      <StepProgress
        steps={inputMethod === 'csv'
          ? [{ id: 1, name: 'Basic Info' }, { id: 2, name: 'Add Items' }, { id: 3, name: 'CSV Review' }, { id: 4, name: 'Review' }]
          : [{ id: 1, name: 'Basic Info' }, { id: 2, name: 'Add Items' }, { id: 3, name: 'Review' }]
        }
        currentStep={step}
      />

      {/* Step 1: Basic Information */}
      {step === 1 && (
        <div className="bg-white p-6 rounded-lg shadow border">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Manifest Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Manifest Name *</label>
              <input
                type="text"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter manifest name"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  formik.touched.name && formik.errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <p className="mt-1 text-xs text-gray-500">
                Provide a name for this manifest, for example your PO number.
              </p>
              {formik.touched.name && formik.errors.name && (
                <p className="mt-1 text-sm text-red-600">{formik.errors.name}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Destination Warehouse *</label>
              <select
                name="destination_warehouse_id"
                value={formik.values.destination_warehouse_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  formik.touched.destination_warehouse_id && formik.errors.destination_warehouse_id ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select destination warehouse</option>
                {warehouses.map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}{warehouse.code ? ` (${warehouse.code})` : ''} - {warehouse.city || ''}{warehouse.state_province ? `, ${warehouse.state_province}` : ''}
                  </option>
                ))}
              </select>
              {formik.touched.destination_warehouse_id && formik.errors.destination_warehouse_id && (
                <p className="mt-1 text-sm text-red-600">{formik.errors.destination_warehouse_id}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Arrival Date</label>
              <input
                type="date"
                name="expected_arrival"
                value={formik.values.expected_arrival}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                onClick={(e) => e.target.showPicker && e.target.showPicker()}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
              />
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              name="notes"
              value={formik.values.notes}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              rows={3}
              placeholder="Additional notes about this manifest"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Receiving Service Instructions. Pre-populated from the
              selected customer's company defaults in the useEffect above;
              admins can override per-manifest. */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Receiving Service Instructions
            </label>
            <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 p-4">
              {[
                ['require_serial_numbers_on_receive', 'Record serial numbers on receipt'],
                ['require_mac_addresses_on_receive', 'Record MAC addresses on receipt'],
                ['relabel_on_receive', 'Relabel units on receipt']
              ].map(([key, label]) => (
                <label key={key} className="flex items-center text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!formik.values.receivingInstructions?.[key]}
                    onChange={(e) => formik.setFieldValue(`receivingInstructions.${key}`, e.target.checked)}
                    className="mr-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  {label}
                </label>
              ))}
              <p className="text-xs text-gray-500">
                Defaulted from this customer's company-level settings. Overrides here apply only to this manifest.
              </p>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={() => setStep(2)}
              disabled={!formik.values.name || !formik.values.destination_warehouse_id}
              className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-md transition-colors"
            >
              Next: Add Items
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Add Items */}
      {step === 2 && (
        <div className="space-y-6">
          {!inputMethod && (
            <div className="bg-white p-6 rounded-lg shadow border">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Add Items</h2>
              <p className="text-sm text-gray-600 mb-6">Choose how you want to add line items to this inbound manifest.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setInputMethod('manual');
                    if (lines.length === 0) addManualLine();
                  }}
                  className="rounded-lg border border-gray-200 p-5 text-left hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
                >
                  <FiPlus className="w-6 h-6 text-indigo-600 mb-3" />
                  <p className="font-semibold text-gray-900">Manual Entry</p>
                  <p className="text-sm text-gray-600 mt-1">Add SKUs one at a time with quantities and optional details.</p>
                </button>
                <button
                  type="button"
                  onClick={() => setInputMethod('csv')}
                  className="rounded-lg border border-gray-200 p-5 text-left hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
                >
                  <FiUpload className="w-6 h-6 text-indigo-600 mb-3" />
                  <p className="font-semibold text-gray-900">CSV Upload</p>
                  <p className="text-sm text-gray-600 mt-1">Upload a spreadsheet when you have many manifest lines.</p>
                </button>
              </div>
            </div>
          )}

          {inputMethod === 'csv' && (
            <div className="bg-white p-6 rounded-lg shadow border">
              <button type="button" onClick={() => setInputMethod(null)} className="mb-4 text-sm text-indigo-600 hover:text-indigo-800">
                &larr; Back to entry selection
              </button>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <FiUpload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <div className="mb-4">
                  <label className="cursor-pointer">
                    <span className="text-blue-600 hover:text-blue-800 font-medium">Choose CSV file</span>
                    <input type="file" accept=".csv,.xlsx,.xls" onChange={handleCsvFileChange} className="hidden" />
                  </label>
                  <p className="text-sm text-gray-500 mt-2">Supported formats: CSV, Excel (.xlsx, .xls)</p>
                </div>
                {csvFile && (
                  <div className="bg-gray-50 rounded-md p-4 mb-4">
                    <p className="text-sm font-medium text-gray-900">Selected: {csvFile.name}</p>
                    <p className="text-sm text-gray-500">Size: {(csvFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                )}
                <button onClick={handleCsvUpload} disabled={!csvFile || uploadCsvMutation.isLoading} className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-md transition-colors">
                  {uploadCsvMutation.isLoading ? <LoadingSpinner /> : 'Upload & Process'}
                </button>
              </div>
            </div>
          )}

          {inputMethod === 'manual' && lines[activeLineIndex] && (
            <div className="bg-white rounded-lg shadow border overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr]">
                <aside className="border-b lg:border-b-0 lg:border-r border-gray-200 bg-gray-50">
                  <div className="p-4 flex items-center justify-between">
                    <h2 className="font-semibold text-gray-900">Items <span className="ml-1 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600">{lines.length}</span></h2>
                    <button type="button" onClick={() => setInputMethod(null)} className="text-xs text-indigo-600 hover:text-indigo-800">Change method</button>
                  </div>
                  <div className="px-4 pb-4">
                    <button type="button" onClick={addManualLine} className="w-full rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">+ Add Item</button>
                  </div>
                  <div className="space-y-2 px-3 pb-4">
                    {lines.map((line, index) => {
                      const status = getLineStatus(line, index);
                      const isActive = index === activeLineIndex;
                      return (
                        <button key={index} type="button" onClick={() => setActiveLineIndex(index)} className={`w-full rounded-lg border p-3 text-left transition-colors ${isActive ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
                          <div className="flex items-start gap-3">
                            <span className="mt-0.5 h-6 w-6 rounded-full bg-gray-100 text-center text-xs font-semibold leading-6 text-gray-600">{index + 1}</span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium text-gray-900">{line.item_name || line.original_sku || `Line Item ${index + 1}`}</span>
                              <span className="block truncate text-xs text-gray-500">{line.original_sku || 'No SKU'}{line.qty_expected ? ` | ${line.qty_expected} ${line.expected_package_type || 'ea'}` : ''}</span>
                            </span>
                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${status === 'Saved' ? 'bg-green-100 text-green-700' : status === 'Needs value' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{status}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </aside>
                <section className="p-6">
                  {(() => {
                    const line = lines[activeLineIndex];
                    const errors = lineErrors[activeLineIndex] || {};
                    const labelClass = 'block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1';
                    const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent';
                    return (
                      <div className="space-y-5">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-3">
                              <h3 className="text-lg font-semibold text-gray-900">Line Item {activeLineIndex + 1} of {lines.length}</h3>
                              {Object.keys(errors).length > 0 && <span className="rounded-full bg-red-100 px-2.5 py-1 text-xs font-medium text-red-700">Needs value</span>}
                            </div>
                            <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Required</p>
                          </div>
                          <button type="button" onClick={() => removeLine(activeLineIndex)} className="text-sm font-medium text-red-600 hover:text-red-700">Remove</button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="relative">
                            <label className={labelClass}>SKU <span className="text-red-500">*</span></label>
                            <div className="relative">
                              <input type="text" value={line.original_sku} onChange={(e) => updateLine(activeLineIndex, 'original_sku', e.target.value)} placeholder="Enter SKU" className={`${inputClass} pr-8 ${errors.original_sku ? 'border-red-400' : ''}`} />
                              <div className="absolute right-2 top-1/2 transform -translate-y-1/2">{getSkuValidationIcon(skuValidation[activeLineIndex])}</div>
                            </div>
                            {errors.original_sku && <p className="text-xs text-red-600 mt-1">{errors.original_sku}</p>}
                            {skuValidation[activeLineIndex] === 'unmatched' && <p className="text-xs text-yellow-600 mt-1">New SKU will be created in customer inventory</p>}
                            {skuValidation[activeLineIndex] === 'valid' && <p className="text-xs text-green-600 mt-1">Matched existing SKU</p>}
                            {activeSkuSuggestionLine === activeLineIndex && (skuSuggestions[activeLineIndex] || []).length > 0 && (
                              <div className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                                {(skuSuggestions[activeLineIndex] || []).map((item) => (
                                  <button key={item.id} type="button" onClick={() => selectSkuSuggestion(activeLineIndex, item)} className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50">
                                    <span className="text-gray-900">{item.productSku || item.product_sku}</span>
                                    {(item.productName || item.product_name) && <span className="ml-2 text-gray-600">{item.productName || item.product_name}</span>}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <div><label className={labelClass}>Item Name <span className="font-normal normal-case text-gray-400">(Optional)</span></label><input type="text" value={line.item_name || ''} onChange={(e) => updateLine(activeLineIndex, 'item_name', e.target.value)} placeholder="Item name" className={inputClass} /></div>
                          <div><label className={labelClass}>Quantity <span className="text-red-500">*</span></label><input type="number" min="1" value={line.qty_expected} onChange={(e) => updateLine(activeLineIndex, 'qty_expected', parseInt(e.target.value) || '')} className={`${inputClass} ${errors.qty_expected ? 'border-red-400' : ''}`} />{errors.qty_expected && <p className="text-xs text-red-600 mt-1">{errors.qty_expected}</p>}</div>
                          <div><label className={labelClass}>Expected Package Type <span className="font-normal normal-case text-gray-400">(Optional)</span></label><select value={line.expected_package_type || 'Each'} onChange={(e) => updateLine(activeLineIndex, 'expected_package_type', e.target.value)} className={inputClass}><option value="Each">Each</option><option value="Carton">Carton</option><option value="Pallet">Pallet</option></select></div>
                        </div>
                        <div className="border-t border-gray-200 pt-4">
                          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Optional Details</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div><label className={labelClass}>Vendor <span className="font-normal normal-case text-gray-400">(Optional)</span></label><input type="text" value={line.vendor || ''} onChange={(e) => updateLine(activeLineIndex, 'vendor', e.target.value)} placeholder="Vendor name" className={inputClass} /></div>
                            <div><label className={labelClass}>Approx. Value ($) <span className="font-normal normal-case text-gray-400">(Optional)</span></label><input type="number" step="1" min="0" value={line.declared_unit_value || ''} onChange={(e) => updateLine(activeLineIndex, 'declared_unit_value', e.target.value === '' ? null : parseFloat(e.target.value))} placeholder="Per unit value" className={inputClass} /></div>
                            <div className="md:col-span-2"><label className={labelClass}>Description <span className="font-normal normal-case text-gray-400">(Optional)</span></label><input type="text" value={line.original_description || ''} onChange={(e) => updateLine(activeLineIndex, 'original_description', e.target.value)} placeholder="Product description" className={inputClass} /></div>
                            <div><label className={labelClass}>Project Name <span className="font-normal normal-case text-gray-400">(Optional)</span></label><input type="text" value={line.project_name || ''} onChange={(e) => updateLine(activeLineIndex, 'project_name', e.target.value)} placeholder="Project name" className={inputClass} /></div>
                            <div><label className={labelClass}>Project Code <span className="font-normal normal-case text-gray-400">(Optional)</span></label><input type="text" value={line.project_code || ''} onChange={(e) => updateLine(activeLineIndex, 'project_code', e.target.value)} placeholder="Project code" className={inputClass} /></div>
                            <div className="md:col-span-2"><label className={labelClass}>Notes <span className="font-normal normal-case text-gray-400">(Optional)</span></label><input type="text" value={line.notes || ''} onChange={(e) => updateLine(activeLineIndex, 'notes', e.target.value)} placeholder="Optional notes" className={inputClass} /></div>
                          </div>
                        </div>
                        <div className="flex justify-end"><button type="button" onClick={() => saveManualLine(activeLineIndex)} className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800">Save Item</button></div>
                      </div>
                    );
                  })()}
                </section>
              </div>
            </div>
          )}

          {false && (
          <>
          <div className="bg-white p-6 rounded-lg shadow border">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Add Items</h2>
            <div className="flex space-x-4 mb-6">
              <button
                onClick={() => setInputMethod('manual')}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  inputMethod === 'manual' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Manual Entry
              </button>
              <button
                onClick={() => setInputMethod('csv')}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  inputMethod === 'csv' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                CSV Upload
              </button>
            </div>

            {/* CSV Upload */}
            {inputMethod === 'csv' && (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <FiUpload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <div className="mb-4">
                  <label className="cursor-pointer">
                    <span className="text-blue-600 hover:text-blue-800 font-medium">Choose CSV file</span>
                    <input type="file" accept=".csv,.xlsx,.xls" onChange={handleCsvFileChange} className="hidden" />
                  </label>
                  <p className="text-sm text-gray-500 mt-2">Supported formats: CSV, Excel (.xlsx, .xls)</p>
                </div>
                {csvFile && (
                  <div className="bg-gray-50 rounded-md p-4 mb-4">
                    <p className="text-sm font-medium text-gray-900">Selected: {csvFile.name}</p>
                    <p className="text-sm text-gray-500">Size: {(csvFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                )}
                <button
                  onClick={handleCsvUpload}
                  disabled={!csvFile || uploadCsvMutation.isLoading}
                  className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-md transition-colors"
                >
                  {uploadCsvMutation.isLoading ? (
                    <div className="flex items-center">
                      <LoadingSpinner />
                      <span className="ml-2">Processing...</span>
                    </div>
                  ) : 'Upload & Process'}
                </button>
                <div className="mt-4 text-left">
                  <h4 className="font-medium text-gray-900 mb-2">CSV Format:</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 font-medium text-gray-700">Field</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-700">Required</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-700">Example</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        <tr><td className="py-2 px-3 font-mono">vendor</td><td className="py-2 px-3 text-gray-600">Optional</td><td className="py-2 px-3 text-gray-600">Acme Co</td></tr>
                        <tr><td className="py-2 px-3 font-mono">original_sku*</td><td className="py-2 px-3 text-red-600 font-medium">Required</td><td className="py-2 px-3 text-gray-600">ABC-123</td></tr>
                        <tr><td className="py-2 px-3 font-mono">item_name</td><td className="py-2 px-3 text-gray-600">Optional</td><td className="py-2 px-3 text-gray-600">Widget A</td></tr>
                        <tr><td className="py-2 px-3 font-mono">qty_expected*</td><td className="py-2 px-3 text-red-600 font-medium">Required</td><td className="py-2 px-3 text-gray-600">500</td></tr>
                        <tr><td className="py-2 px-3 font-mono">expected_package_type</td><td className="py-2 px-3 text-gray-600">Optional</td><td className="py-2 px-3 text-gray-600">Carton, Pallet, Each</td></tr>
                        <tr><td className="py-2 px-3 font-mono">notes</td><td className="py-2 px-3 text-gray-600">Optional</td><td className="py-2 px-3 text-gray-600">Handle with care</td></tr>
                      </tbody>
                    </table>
                    <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
                      <p className="text-sm text-blue-800 mb-2"><strong>Sample CSV:</strong></p>
                      <div className="bg-white rounded p-2 border overflow-x-auto">
                        <code className="text-xs whitespace-nowrap block">vendor,original_sku,item_name,qty_expected,expected_package_type,notes</code>
                        <code className="text-xs whitespace-nowrap block text-gray-600">Acme Co,ABC-123,Widget A,500,Carton,Handle with care</code>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Manual Entry */}
            {inputMethod === 'manual' && (
              <div>
                {lines.length === 0 && (
                  <div className="text-center py-8">
                    <FiPackage className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">No items added yet</p>
                    <button
                      onClick={addManualLine}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md flex items-center mx-auto"
                    >
                      <FiPlus className="w-4 h-4 mr-2" />
                      Add First Item
                    </button>
                  </div>
                )}
                {lines.length > 0 && (
                  <div className="space-y-4">
                    {lines.map((line, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg">
                        {!expandedItems[index] && (
                          <div className="p-4">
                            <div className="flex justify-between items-center">
                              <div className="flex-1">
                                <h4 className="font-medium text-gray-900">
                                  Line Item #{index + 1} - {line.original_sku || 'No SKU'}
                                </h4>
                                <p className="text-sm text-gray-600">
                                  {line.item_name && `${line.item_name} \u2022 `}
                                  Qty: {line.qty_expected} \u2022 {line.expected_package_type || 'Each'}
                                </p>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button onClick={() => toggleItemExpansion(index)} className="text-blue-600 hover:text-blue-800 p-1" title="Expand item">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>
                                <button onClick={() => removeLine(index)} className="text-red-600 hover:text-red-800 p-1" title="Delete item">
                                  <FiTrash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                        {expandedItems[index] && (
                          <div className="p-4">
                            <div className="flex justify-between items-start mb-4">
                              <h4 className="font-medium text-gray-900">Line Item #{index + 1}</h4>
                              <div className="flex items-center space-x-2">
                                <button onClick={() => toggleItemExpansion(index)} className="text-blue-600 hover:text-blue-800 p-1" title="Collapse item">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                  </svg>
                                </button>
                                <button onClick={() => removeLine(index)} className="text-red-600 hover:text-red-800 p-1" title="Delete item">
                                  <FiTrash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <div className="space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="relative">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">SKU *</label>
                                  <div className="relative">
                                    <input
                                      type="text"
                                      value={line.original_sku}
                                      onChange={(e) => updateLine(index, 'original_sku', e.target.value)}
                                      placeholder="Enter SKU"
                                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                      {getSkuValidationIcon(skuValidation[index])}
                                    </div>
                                  </div>
                                  {skuValidation[index] === 'unmatched' && (
                                    <p className="text-xs text-yellow-600 mt-1">New SKU — will be created in customer inventory</p>
                                  )}
                                  {skuValidation[index] === 'valid' && (
                                    <p className="text-xs text-green-600 mt-1">Matched existing SKU</p>
                                  )}
                                  {activeSkuSuggestionLine === index && (skuSuggestions[index] || []).length > 0 && (
                                    <div className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                                      {(skuSuggestions[index] || []).map((item) => (
                                        <button
                                          key={item.id}
                                          type="button"
                                          onClick={() => selectSkuSuggestion(index, item)}
                                          className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                                        >
                                          <span className="font-mono text-gray-900">{item.productSku || item.product_sku}</span>
                                          {(item.productName || item.product_name) && (
                                            <span className="ml-2 text-gray-600">{item.productName || item.product_name}</span>
                                          )}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Item Name</label>
                                  <input
                                    type="text"
                                    value={line.item_name || ''}
                                    onChange={(e) => updateLine(index, 'item_name', e.target.value)}
                                    placeholder="Item name"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                                  <input
                                    type="number"
                                    min="1"
                                    value={line.qty_expected}
                                    onChange={(e) => updateLine(index, 'qty_expected', parseInt(e.target.value) || 1)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Expected Package Type</label>
                                  <select
                                    value={line.expected_package_type || 'Each'}
                                    onChange={(e) => updateLine(index, 'expected_package_type', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  >
                                    <option value="Each">Each</option>
                                    <option value="Carton">Carton</option>
                                    <option value="Pallet">Pallet</option>
                                  </select>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
                                  <input
                                    type="text"
                                    value={line.vendor || ''}
                                    onChange={(e) => updateLine(index, 'vendor', e.target.value)}
                                    placeholder="Vendor name"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                  <input
                                    type="text"
                                    value={line.original_description || ''}
                                    onChange={(e) => updateLine(index, 'original_description', e.target.value)}
                                    placeholder="Product description"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  />
                                </div>
                              </div>
                              {/* Insurance value + project tracking.
                                  Mirrors the customer manifest form so
                                  admin-created manifests capture the
                                  same per-line metadata the customer
                                  form already handles. */}
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Approx. Value ($)</label>
                                  <input
                                    type="number"
                                    step="1"
                                    min="0"
                                    value={line.declared_unit_value ?? ''}
                                    onChange={(e) => updateLine(index, 'declared_unit_value', e.target.value === '' ? null : parseFloat(e.target.value))}
                                    placeholder="Per-unit value for insurance"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
                                  <input
                                    type="text"
                                    value={line.project_name || ''}
                                    onChange={(e) => updateLine(index, 'project_name', e.target.value)}
                                    placeholder="e.g., University of Toronto"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Project Code</label>
                                  <input
                                    type="text"
                                    value={line.project_code || ''}
                                    onChange={(e) => updateLine(index, 'project_code', e.target.value)}
                                    placeholder="e.g., UOFT2026"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                <input
                                  type="text"
                                  value={line.notes || ''}
                                  onChange={(e) => updateLine(index, 'notes', e.target.value)}
                                  placeholder="Optional notes"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={addManualLine}
                      className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 hover:bg-blue-50 transition-colors"
                    >
                      <FiPlus className="w-5 h-5 text-gray-400 mx-auto mb-2" />
                      <span className="text-gray-600">Add Another Item</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          </>
          )}

          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-2 rounded-md transition-colors">
              Back
            </button>
            <button
              onClick={handleProceedToReview}
              disabled={lines.length === 0}
              className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-md transition-colors"
            >
              {inputMethod === 'csv' ? 'Review CSV' : 'Next: Review'}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: CSV Review */}
      {step === 3 && inputMethod === 'csv' && csvPreview && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow border">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">CSV Validation Results</h2>
            {csvPreview.validation && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <FiPackage className="w-6 h-6 text-blue-600 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-blue-800">Total Lines</p>
                      <p className="text-2xl font-bold text-blue-600">{csvPreview.validation.totalLines}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <FiCheck className="w-6 h-6 text-green-600 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-green-800">Matched SKUs</p>
                      <p className="text-2xl font-bold text-green-600">{csvPreview.validation.matchedLines}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <FiAlertCircle className="w-6 h-6 text-yellow-600 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">New Items</p>
                      <p className="text-2xl font-bold text-yellow-600">{csvPreview.validation.unmatchedLines}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow border overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Item Details</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Line</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {lines.map((line, index) => (
                    <tr key={index} className={line.match_status === 'matched' ? 'bg-green-50' : 'bg-yellow-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{line.line_number}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{line.original_sku}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{line.item_name || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{line.qty_expected}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {line.match_status === 'matched' && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <FiCheck className="w-3 h-3 mr-1" /> Existing Item
                          </span>
                        )}
                        {line.match_status === 'unmatched' && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <FiPlus className="w-3 h-3 mr-1" /> New Item
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-between">
            <button onClick={() => setStep(2)} className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-2 rounded-md transition-colors">
              Back to Items
            </button>
            <button onClick={() => setStep(4)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-md transition-colors">
              Continue to Review
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Final Review & Submit */}
      {(step === 4 || (step === 3 && inputMethod === 'manual')) && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow border">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Manifest Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Details</h3>
                <dl className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Customer:</dt>
                    <dd className="text-gray-900">{company?.name || 'N/A'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Name:</dt>
                    <dd className="text-gray-900">{formik.values.name}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Warehouse:</dt>
                    <dd className="text-gray-900">
                      {warehouses.find(w => w.id === formik.values.destination_warehouse_id)?.name || 'N/A'}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Estimated Arrival:</dt>
                    <dd className="text-gray-900">{formik.values.expected_arrival || 'Not specified'}</dd>
                  </div>
                </dl>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Items Summary</h3>
                <dl className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Total Items:</dt>
                    <dd className="text-gray-900">{lines.length}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Total Quantity:</dt>
                    <dd className="text-gray-900">{lines.reduce((sum, line) => sum + (line.qty_expected || 0), 0)}</dd>
                  </div>
                </dl>
              </div>
            </div>
            {formik.values.notes && (
              <div className="mt-4">
                <h3 className="font-medium text-gray-900 mb-2">Notes</h3>
                <p className="text-sm text-gray-600">{formik.values.notes}</p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow border overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Items Review</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Packaging</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {lines.map((line, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{line.original_sku || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{line.item_name || line.original_description || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{line.qty_expected}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{line.expected_package_type || 'Each'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setStep(inputMethod === 'csv' ? 3 : 2)}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-2 rounded-md transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={createProjectMutation.isLoading}
              className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-md transition-colors inline-flex items-center"
              type="button"
            >
              {createProjectMutation.isLoading ? (
                <>
                  <LoadingSpinner />
                  <span className="ml-2">Creating...</span>
                </>
              ) : 'Create Manifest'}
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AdminCreateInboundProject;
